import { BodyScrollOptions } from './types';
import {
    BOTTOM_BAR_CHECK_DELAY,
    createTouchMoveHandler,
    handleTouchStart,
    preventDocumentTouchMove,
    setInitialClientY,
} from './utils';
import { getActiveLocks } from './active_locks';

let originalBodyStyles: {
    overflow?: string;
    position?: string;
    top?: string;
    left?: string;
    width?: string;
    paddingRight?: string;
} = {};

let originalDocumentStyles: {
    overflow?: string;
} = {};

let documentListenerAdded = false;

/**
 * Applies styles on body and documentElement to lock scrolling
 *
 * @param options - Additional options for the scroll lock
 */
const disableScrollDefault = (options: BodyScrollOptions = {}): void => {
    // Preserve scrollbar gap if requested
    if (options.reserveScrollBarGap && originalBodyStyles.paddingRight === undefined) {
        const scrollBarGap = window.innerWidth - document.documentElement.clientWidth;
        if (scrollBarGap > 0) {
            const currentPaddingRight = parseInt(
                window.getComputedStyle(document.body).getPropertyValue('padding-right'),
                10
            );
            originalBodyStyles.paddingRight = document.body.style.paddingRight;
            document.body.style.paddingRight = `${currentPaddingRight + scrollBarGap}px`;
        }
    }

    // Set overflow: hidden on body and documentElement
    if (originalBodyStyles.overflow === undefined) {
        originalBodyStyles.overflow = document.body.style.overflow;
        originalDocumentStyles.overflow = document.documentElement.style.overflow;

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
    }
};

/**
 * Applies styles on body and documentElement to restore scrollability
 */
const enableScrollDefault = (): void => {
    // Restore padding-right
    if (originalBodyStyles.paddingRight !== undefined) {
        document.body.style.paddingRight = originalBodyStyles.paddingRight;
        originalBodyStyles.paddingRight = undefined;
    }

    // Restore overflow styles
    if (originalBodyStyles.overflow !== undefined) {
        document.body.style.overflow = originalBodyStyles.overflow;
        document.documentElement.style.overflow = originalDocumentStyles.overflow || '';

        originalBodyStyles.overflow = undefined;
        originalDocumentStyles.overflow = undefined;
    }
};

/**
 * IOS: Set position: fixed with calculated top/left values
 *
 * @param targetElement - The element that should remain scrollable
 *
 * @note setTimeout and requestAnimationFrame are used to ensure that all UI elements are rendered before certain calculations are made.
 */
const disableScrollIOS = (targetElement: HTMLElement): void => {
    // Attempt to check if the bottom bar appeared due to the position change
    if (originalBodyStyles.position === undefined) {
        window.requestAnimationFrame(() => {
            const { scrollY, scrollX, innerHeight } = window;

            // Save original styles
            originalBodyStyles.position = document.body.style.position;
            originalBodyStyles.top = document.body.style.top;
            originalBodyStyles.left = document.body.style.left;
            originalBodyStyles.width = document.body.style.width;

            // Apply position: fixed with negative top/left to maintain scroll position
            document.body.style.position = 'fixed';
            document.body.style.top = `${-scrollY}px`;
            document.body.style.left = `${-scrollX}px`;
            document.body.style.width = `100%`;

            // Handle iOS bottom bar compensation
            setTimeout(() => {
                window.requestAnimationFrame(() => {
                    const bottomBarHeight = innerHeight - window.innerHeight; // The two innerHeights are differing due to the timing difference

                    if (bottomBarHeight && scrollY >= window.innerHeight) {
                        // Move the content further up so that the bottom bar doesn't hide it
                        document.body.style.top = `${-(scrollY + bottomBarHeight)}px`;
                    }
                });
            }, BOTTOM_BAR_CHECK_DELAY);
        });
    }

    // Add touch event handlers to target element
    targetElement.ontouchstart = handleTouchStart;
    targetElement.ontouchmove = createTouchMoveHandler(targetElement);

    // Add document-level touch handler
    if (!documentListenerAdded) {
        try {
            document.addEventListener('touchmove', preventDocumentTouchMove, { passive: false } as any);
        } catch (e) {
            document.addEventListener('touchmove', preventDocumentTouchMove, false);
        }
        documentListenerAdded = true;
    }
};

/**
 * IOS: Restore position: static and scroll to original position
 *
 * @param targetElement - The element that should remain scrollable
 */
const enableScrollIOS = (targetElement: HTMLElement): void => {
    // Remove touch handlers from target element
    targetElement.ontouchstart = null;
    targetElement.ontouchmove = null;

    if (getActiveLocks().length !== 0) {
        return;
    }

    // If no more locks, restore position styles and remove document listener
    if (originalBodyStyles.position !== undefined) {
        const scrollY = parseInt(window.getComputedStyle(document.body).top, 10);
        const scrollX = parseInt(window.getComputedStyle(document.body).left, 10);

        document.body.style.position = originalBodyStyles.position;
        document.body.style.top = originalBodyStyles.top || '';
        document.body.style.left = originalBodyStyles.left || '';
        document.body.style.width = originalBodyStyles.width || '';

        window.scrollTo(scrollX, scrollY);

        originalBodyStyles = {};
    }

    // Remove document touch listener
    if (documentListenerAdded) {
        document.removeEventListener('touchmove', preventDocumentTouchMove);
        documentListenerAdded = false;
    }

    setInitialClientY(-1);
};

export const lockStrategy = {
    ios: {
        enable: enableScrollIOS,
        disable: disableScrollIOS,
    },
    default: {
        enable: enableScrollDefault,
        disable: disableScrollDefault,
    },
};
